import { useEffect, useState } from "react";
import { API_URL } from '../../../../../constants';
import axios from 'axios';
import {Box, AppBar, Toolbar, Typography, Container, CircularProgress, Button,} from "@mui/material";
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ZoneSummary } from './ZoneSummary';
import { TornadoChart } from './TornadoChart';
import { Recommendations } from './Recommendations';

const BACKEND_URL = API_URL
const API = `${BACKEND_URL}/`;

export default function PhenoAgeDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}phenoages`);
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch PhenoAge data:', e);
      setError('Unable to load cockpit data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Loading cockpit data...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ bgcolor: 'background.paper', borderRadius: 4, border: 1, borderColor: 'divider', p: 4, textAlign: 'center', maxWidth: 360 }}>
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 3 }}>{error}</Typography>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchData} sx={{ bgcolor: '#1c1917', '&:hover': { bgcolor: '#292524' } }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
<>
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(16px)', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ maxWidth: 1280, mx: 'auto', width: '100%', px: { xs: 2, md: 6 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: '#1c1917', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MonitorHeartOutlinedIcon sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontSize: '1.05rem', lineHeight: 1.1, color: 'text.primary' }}>
                PhenoAge
              </Typography>
              <Typography variant="subtitle2" sx={{ fontSize: '0.6rem', color: 'text.secondary', letterSpacing: '0.15em' }}>
                Biological Age Cockpit
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: { xs: 'none', sm: 'block' } }}>
              Levine et al. 2018
            </Typography>
            <Box className="animate-pulse-glow" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, md: 6 } }}>
        {/* Zone 1 */}
        <Box sx={{ mb: 4 }}>
          <ZoneSummary data={data} />
        </Box>

        {/* Zone 2 + Zone 3 Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: { xs: 3, md: 4 } }}>
          <TornadoChart sensitivityData={data.sensitivity_analysis} patternSummary={data.pattern_summary} />
          <Recommendations recommendations={data.recommendations} />
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 8, pt: 4, borderTop: 1, borderColor: 'divider', pb: 4 }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.disabled', maxWidth: 720, lineHeight: 1.7 }}>
            PhenoAge is a validated measure of biological aging based on clinical chemistry biomarkers.
            This dashboard is for informational purposes only and does not constitute medical advice.
            Consult your healthcare provider before making any health decisions based on these results.
          </Typography>
        </Box>
      </Container>
    </Box>
    
    </>
  );
}
